import {CommentBox, type CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {ReadonlyCommentBox} from '@github-ui/comment-box/ReadonlyCommentBox'
import type {Subject} from '@github-ui/comment-box/subject'
import {ContributorFooter} from '@github-ui/contributor-footer/ContributorFooter'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {type CloseButtonState, IssueActions} from '@github-ui/issue-actions/IssueActions'
import {useCloseIssueMutation, useReopenIssueMutation} from '@github-ui/issue-actions/UpdateIssueStateMutation'
import {canBeOptimisticallyParsed} from '@github-ui/md2html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {testIdProps} from '@github-ui/test-id-props'
import {useViewport} from '@github-ui/use-viewport'
import {ChevronDownIcon, LockIcon, PencilIcon} from '@primer/octicons-react'
import {Heading, IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/button-labels'
import {IDS} from '../../constants/dom-elements'
import {ERRORS} from '../../constants/errors'
import {REACTION_CONTENT, SUBSCRIPTION_FORM_ACTION} from '../../constants/graphql-enums'
import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import {useComposerResize} from '../../hooks/use-composer-resize'
import {useLowQualityWarning} from '../../hooks/use-low-quality-warning'
import {type MarkdownComposerRef, useMarkdownBody} from '../../hooks/use-markdown-body'
import {useMetaAndShiftKeys} from '../../hooks/use-meta-and-shift-keys'
import {getAddCommentOptimisticNode} from '../../mutations/add-comment-mutation'
import {useAddCommentMutation} from '../../mutations/use-comment-mutations'
import {blockedCommentingReason} from '../../utils/blocked-commenting-reason'
import sharedStyles from '../Shared.module.css'
import {ComposerResizeHandle} from './ComposerResizeHandle'
import type {
  IssueCommentComposerSecondaryNode,
  IssueCommentComposerSecondaryRepositoryNode,
} from './issue-comment-composer-secondary-query-types'
import type {
  IssueCommentComposerIssueNode,
  IssueCommentComposerRepositoryNode,
  IssueCommentComposerRepositoryOwnerNode,
  IssueCommentComposerViewerNode,
} from './issue-viewer-view-query-types'
import styles from './IssueCommentComposer.module.css'

export type IssueCommentComposerSecondaryFragment = RelayCompat<
  Pick<
    IssueCommentComposerSecondaryNode,
    | 'viewerCanReopen'
    | 'viewerCanClose'
    | 'authorAssociation'
    | 'viewerThreadSubscriptionFormAction'
    | 'reactionGroups'
    | 'discussion'
    | 'parent'
  > & {
    readonly repository: Pick<
      IssueCommentComposerSecondaryRepositoryNode,
      | 'isLocked'
      | 'slashCommandsEnabled'
      | 'viewerCanCreateIssues'
      | 'codeOfConductFileUrl'
      | 'securityPolicyUrl'
      | 'contributingFileUrl'
      | 'supportFileUrl'
    >
  }
>

// RELAY-CLEANUP(IssueViewerViewer): RelayCompat widens `name` from `string | null` to
// `string | null | undefined` so the still-Relay parent's data is assignable.
// Remove when IssueViewerViewer leaves Relay.
export type IssueCommentComposerViewerFragment = RelayCompat<
  Pick<IssueCommentComposerViewerNode, 'id' | 'login' | 'name' | 'avatarUrl'>
>

// RELAY-CLEANUP(IssueViewerIssue): RelayCompat widens nullable fields so the still-Relay parent's data is
// assignable. Remove when IssueViewerIssue leaves Relay.
export type IssueCommentComposerFragment = RelayCompat<
  Pick<
    IssueCommentComposerIssueNode,
    'id' | 'number' | 'locked' | 'viewerCanComment' | 'databaseId' | 'state' | 'stateReason' | 'author'
  > & {
    readonly repository: Pick<
      IssueCommentComposerRepositoryNode,
      | 'id'
      | 'databaseId'
      | 'name'
      | 'isArchived'
      | 'isPrivate'
      | 'nameWithOwner'
      | 'viewerCanInteract'
      | 'viewerInteractionLimitReasonHTML'
    > & {
      readonly owner: Pick<IssueCommentComposerRepositoryOwnerNode, '__typename' | 'id' | 'login' | 'url'>
    }
  }
>

type IssueCommentComposerBaseProps = {
  onChange: () => void
  onSave: () => void
  onCancel: () => void
  onNewIssueComment?: () => void
  onStateChange?: () => void
  fileUploadsEnabled: boolean
  insideSidePanel: boolean
}

type IssueCommentComposerProps = IssueCommentComposerBaseProps & {
  issue: IssueCommentComposerFragment
  issueSecondary?: IssueCommentComposerSecondaryFragment
  viewer: IssueCommentComposerViewerFragment
  ref?: React.Ref<MarkdownComposerRef>
}

export const IssueCommentComposer = ({
  ref,
  onChange,
  onSave,
  onCancel,
  onNewIssueComment,
  onStateChange,
  issue: data,
  issueSecondary: secondaryData,
  viewer: viewerData,
  fileUploadsEnabled,
  insideSidePanel,
}: IssueCommentComposerProps) => {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  // If the secondary data is not available, we default to false
  const slashCommandsEnabled = secondaryData?.repository.slashCommandsEnabled ?? false

  const {id: issueId} = data
  const commentBoxRef = useRef<CommentBoxHandle>(null)
  const collapsedFieldRef = useRef<HTMLButtonElement>(null)
  const commentComposerRef = useRef<HTMLDivElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const repositoryDatabaseId = data.repository.databaseId!
  const isRepoArchived = data.repository.isArchived
  const environment = useRelayEnvironment()
  const addComment = useAddCommentMutation({
    connection: {ownerId: data.id, connectionPath: ['timelineItems']},
    environment,
  })
  const reopenIssue = useReopenIssueMutation({
    owner: data.repository.owner.login,
    repo: data.repository.name,
    issueNumber: data.number,
    environment,
  })
  const closeIssue = useCloseIssueMutation({
    owner: data.repository.owner.login,
    repo: data.repository.name,
    issueNumber: data.number,
    environment,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [composerError, setComposerError] = useState<{issueId: string; message: string} | null>(null)
  const composerErrorMessage = composerError?.issueId === issueId ? composerError.message : undefined
  const [closeButtonState, setCloseButtonState] = useState<CloseButtonState>('OPEN')
  const cantCommentReason = blockedCommentingReason(
    isRepoArchived,
    data.locked,
    !data.repository.viewerCanInteract ? data.repository.viewerInteractionLimitReasonHTML : null,
    'issue',
    secondaryData?.repository?.isLocked ?? false,
  )
  const innerComposerRef = useRef<MarkdownComposerRef>(null)
  const {metaAndShiftKeysPressed} = useMetaAndShiftKeys()
  // Disable the sticky composer on phones (touch device with a narrow viewport): its
  // sticky positioning and resize handle fight the on-screen keyboard. width is undefined
  // until the viewport is measured client-side, so it defaults to the desktop experience.
  const {width, isTouchDevice} = useViewport()
  const isMobile = Boolean(isTouchDevice) && width !== undefined && width < 768
  const stickyCommentComposer = isFeatureEnabled('issues_react_sticky_comment_composer') && !isMobile
  const [composerExpanded, setComposerExpanded] = useState(true)
  const [isComposerFocused, setIsComposerFocused] = useState(false)
  const expandComposer = useCallback(() => {
    setComposerExpanded(true)
    requestAnimationFrame(() => commentBoxRef.current?.focus())
  }, [setComposerExpanded])
  const collapseComposer = useCallback(() => {
    setComposerExpanded(false)
    requestAnimationFrame(() => collapsedFieldRef.current?.focus())
  }, [setComposerExpanded])
  // Track focus so the composer stays active (sticky) while focused, and auto-expand it when
  // focus lands on the collapsed wrapper itself. Only wired up for the sticky experience.
  const handleComposerFocus = useCallback(
    (e: React.FocusEvent) => {
      setIsComposerFocused(true)
      if (!composerExpanded && e.target === e.currentTarget) expandComposer()
    },
    [composerExpanded, expandComposer],
  )
  const handleComposerBlur = useCallback((e: React.FocusEvent) => {
    // Only clear focus once it leaves the composer subtree entirely.
    if (!e.currentTarget.contains(e.relatedTarget)) setIsComposerFocused(false)
  }, [])
  const {markdownBody, resetMarkdownBody, markdownValidationResult, handleMarkdownBodyChanged} = useMarkdownBody({
    commentBoxRef,
    markdownComposerRef: innerComposerRef,
    onChange,
    onCancel,
    referenceId: issueId,
    insidePortal: insideSidePanel,
    onBeforeFocusShortcut: stickyCommentComposer ? expandComposer : undefined,
  })
  // The composer stays "active" (sticky and height-retained) while it has a draft or is
  // focused, so clearing the text while still focused doesn't drop it back to the bottom.
  const hasDraft = markdownBody.length > 0
  const composerActive = hasDraft || isComposerFocused
  const composerResize = useComposerResize({
    enabled: stickyCommentComposer,
    containerRef: commentComposerRef,
    active: composerActive,
    onCollapse: collapseComposer,
  })

  // Compute viewer's existing state for low-quality warning actions
  // These fields load from secondary query, so default to false until loaded
  const thumbsUpGroup = secondaryData?.reactionGroups?.find(group => group.content === REACTION_CONTENT.THUMBS_UP)
  const viewerHasLiked = thumbsUpGroup?.viewerHasReacted ?? false
  const viewerIsSubscribed = secondaryData?.viewerThreadSubscriptionFormAction === SUBSCRIPTION_FORM_ACTION.UNSUBSCRIBE

  const {
    banner: lowQualityWarningBanner,
    bannerPaddingClassName,
    trackSubmittedWithWarning,
  } = useLowQualityWarning({
    markdownBody,
    issueId: data.id,
    viewerHasLiked,
    viewerIsSubscribed,
    isViewerDataLoaded: !!secondaryData,
    onDismiss: useCallback(() => commentBoxRef.current?.focus(), []),
  })

  const onCommentSave = useCallback(
    (skipStateChange = false) => {
      const withOptimisticResponse = canBeOptimisticallyParsed(markdownBody)
      setComposerError(null)
      setIsSubmitting(true)
      if (markdownBody?.length > 0) {
        // Track analytics if low-quality warning was shown
        trackSubmittedWithWarning(markdownBody)
        // Store the original markdown body value to restore if mutation fails
        const originalMarkdownBody = markdownBody

        // Reset the markdown body before starting the mutation
        resetMarkdownBody()
        // End the active composing session: clear focus state too, so the composer drops
        // back to its non-sticky resting state. Submitting can disable/replace the focused
        // control without firing onBlur, which would otherwise leave isComposerFocused stale
        // and keep the (now empty) composer sticky. On error the draft is restored below,
        // which makes it active again.
        setIsComposerFocused(false)

        const optimisticInput = secondaryData
          ? {
              commentAuthor: {
                id: viewerData.id,
                login: viewerData.login,
                avatarUrl: viewerData.avatarUrl,
                name: viewerData.name,
              },
              issue: data,
              repository: {
                ...data.repository,
                slashCommandsEnabled: secondaryData.repository.slashCommandsEnabled,
                viewerCanCreateIssues: secondaryData.repository.viewerCanCreateIssues,
              },
              authorAssociation: secondaryData.authorAssociation,
            }
          : undefined

        const onError = () => {
          setIsSubmitting(false)

          // Restore the original markdown body value if mutation fails
          handleMarkdownBodyChanged(originalMarkdownBody)

          setComposerError({issueId, message: ERRORS.couldNotComment})
        }
        const onCompleted = () => {
          setIsSubmitting(false)
          onNewIssueComment?.()
        }

        addComment({
          route: {
            owner: data.repository.owner.login,
            repo: data.repository.name,
            number: data.number,
          },
          body: originalMarkdownBody,
          optimisticNode: getAddCommentOptimisticNode(optimisticInput, withOptimisticResponse, originalMarkdownBody),
          onError,
          onCompleted,
        })
      }

      // Do not modify the issue state when we are already modifying the issue state elsewhere
      if (metaAndShiftKeysPressed && !skipStateChange) {
        if (closeButtonState === 'CLOSED' && secondaryData?.viewerCanClose) {
          closeIssue(
            {
              issueId: data?.id,
              newStateReason: 'COMPLETED',
              parentIssueId: secondaryData?.parent?.id,
            },
            {
              onError: () => setComposerError(prev => prev ?? {issueId, message: ERRORS.couldNotCloseIssue}),
              onCompleted: () => {
                onNewIssueComment?.()
                onStateChange?.()
              },
            },
          )
        } else if (closeButtonState === 'NOT_PLANNED' && secondaryData?.viewerCanClose) {
          closeIssue(
            {
              issueId: data?.id,
              newStateReason: 'NOT_PLANNED',
              parentIssueId: secondaryData?.parent?.id,
            },
            {
              onError: () => setComposerError(prev => prev ?? {issueId, message: ERRORS.couldNotCloseIssue}),
              onCompleted: () => {
                onNewIssueComment?.()
                onStateChange?.()
              },
            },
          )
        } else if (closeButtonState === 'OPEN' && secondaryData?.viewerCanReopen) {
          reopenIssue(data.id, secondaryData?.parent?.id, {
            onError: () => setComposerError(prev => prev ?? {issueId, message: ERRORS.couldNotReopenIssue}),
            onCompleted: () => {
              onNewIssueComment?.()
              onStateChange?.()
            },
          })
        }
      }

      onSave()
    },
    [
      markdownBody,
      metaAndShiftKeysPressed,
      onSave,
      issueId,
      data,
      viewerData.id,
      viewerData.login,
      viewerData.avatarUrl,
      viewerData.name,
      secondaryData,
      resetMarkdownBody,
      handleMarkdownBodyChanged,
      addComment,
      onNewIssueComment,
      onStateChange,
      closeButtonState,
      closeIssue,
      reopenIssue,
      trackSubmittedWithWarning,
    ],
  )

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue_comment',
      id: {
        id: data.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        databaseId: data.databaseId!,
      },
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: data.repository.nameWithOwner,
        slashCommandsEnabled,
      },
    }
  }, [data.databaseId, data.id, data.repository.nameWithOwner, repositoryDatabaseId, slashCommandsEnabled])

  const handleActionButtonUpdate = useCallback(() => {
    if (markdownValidationResult && markdownValidationResult.isValid) {
      onCommentSave(true)
    }
  }, [onCommentSave, markdownValidationResult])

  const actions = (
    <IssueActions
      actionState={data}
      onAction={handleActionButtonUpdate}
      hasComment={markdownBody.length > 0}
      buttonSize="medium"
      closeButtonState={closeButtonState}
      setCloseButtonState={setCloseButtonState}
      viewerCanReopen={secondaryData?.viewerCanReopen}
      viewerCanClose={secondaryData?.viewerCanClose}
      onStateChange={onStateChange}
      parentIssueId={secondaryData?.parent?.id}
    />
  )

  const issuesReactLazyLoad = isFeatureEnabled('issues_lazy_load_comment_box_suggestions')
  const viewerCanComment = data.viewerCanComment && data.repository.viewerCanInteract && !secondaryData?.discussion

  // Auto-expand when the imperative handle triggers focus/appendText/setText
  useImperativeHandle(
    ref,
    () => ({
      appendText: (text: string) => {
        expandComposer()
        innerComposerRef.current?.appendText(text)
      },
      setText: (text: string) => {
        expandComposer()
        innerComposerRef.current?.setText(text)
      },
      focus: () => {
        expandComposer()
        innerComposerRef.current?.focus()
      },
    }),
    [expandComposer],
  )

  const contributingFileUrl = secondaryData?.repository.contributingFileUrl || undefined
  const codeOfConductFileUrl = secondaryData?.repository.codeOfConductFileUrl || undefined
  const securityPolicyUrl = secondaryData?.repository.securityPolicyUrl || undefined
  const supportFileUrl = secondaryData?.repository.supportFileUrl || undefined
  const renderContributorFooter = !!secondaryData?.repository
  const {isBrowser} = useRuntimeEnvironment()
  const inlineAvatars = isFeatureEnabled('issue_inline_avatars')

  const commentBoxContent = (
    <>
      {isBrowser &&
        (!data.viewerCanComment || !data.repository.viewerCanInteract || secondaryData?.discussion ? (
          <ReadonlyCommentBox
            reason={
              secondaryData?.discussion ? (
                <CommentDisabledDueToDiscussion discussionUrl={secondaryData?.discussion?.url ?? ''} />
              ) : (
                cantCommentReason
              )
            }
            icon={LockIcon}
            {...testIdProps(TEST_IDS.readonlyCommentBox(TEST_IDS.commentComposer))}
          />
        ) : (
          <CommentBox
            ref={commentBoxRef}
            placeholder={LABELS.newCommentPlaceholder}
            label={LABELS.newComment}
            disabled={isSubmitting}
            subject={subject}
            infoBanner={lowQualityWarningBanner}
            value={markdownBody}
            onChange={handleMarkdownBodyChanged}
            actions={actions}
            validationResult={markdownValidationResult}
            markdownErrorMessage={composerErrorMessage}
            saveButtonText={BUTTON_LABELS.comment}
            saveButtonTrailingIcon={false}
            onSave={() => onCommentSave()}
            fileUploadsEnabled={fileUploadsEnabled}
            buttonSize="medium"
            labelledBy="comment-composer-heading"
            teamHovercardsEnabled
            maxHeightLines={stickyCommentComposer ? 10 : undefined}
            markdownSuggestionsFetchMethod={issuesReactLazyLoad ? 'lazy' : 'eager'}
            className={sharedStyles.CommentBox}
            {...testIdProps(TEST_IDS.commentBox(TEST_IDS.commentComposer))}
          />
        ))}
      {renderContributorFooter && (
        <ContributorFooter
          contributingFileUrl={contributingFileUrl}
          codeOfConductFileUrl={codeOfConductFileUrl}
          securityPolicyUrl={securityPolicyUrl}
          supportFileUrl={supportFileUrl}
          className={styles.ContributorFooter}
        />
      )}
    </>
  )

  const resizeHandle =
    stickyCommentComposer && viewerCanComment && composerExpanded && composerActive ? (
      <ComposerResizeHandle isResizing={composerResize.isResizing} handleProps={composerResize.handleProps} />
    ) : null

  return (
    <div
      data-testid={TEST_IDS.commentComposer}
      data-jump-to-bottom-target={stickyCommentComposer || undefined}
      tabIndex={stickyCommentComposer ? -1 : undefined}
      role={stickyCommentComposer ? 'region' : undefined}
      aria-label={stickyCommentComposer ? 'Add a comment' : undefined}
      className={clsx(
        styles.commentComposerWrapper,
        inlineAvatars && styles.commentComposerWrapper_inlineAvatars,
        stickyCommentComposer && viewerCanComment && composerActive && styles.sticky,
        stickyCommentComposer && viewerCanComment && !composerExpanded && styles.collapsed,
      )}
      data-inside-side-panel={insideSidePanel}
      data-resized={composerResize.isResized || composerResize.isResizing || undefined}
      id={IDS.issueCommentComposer}
      ref={commentComposerRef}
      style={composerResize.style}
      onFocus={stickyCommentComposer ? handleComposerFocus : undefined}
      onBlur={stickyCommentComposer ? handleComposerBlur : undefined}
    >
      {stickyCommentComposer && viewerCanComment && !composerExpanded && (
        <button
          type="button"
          className={clsx(styles.collapsedField, inlineAvatars && styles.collapsedField_inlineAvatars)}
          ref={collapsedFieldRef}
          onClick={expandComposer}
          aria-label={markdownBody.length > 0 ? LABELS.continueEditing : LABELS.newCommentPlaceholder}
        >
          {!inlineAvatars && (
            <div className={styles.collapsedAvatar}>
              <GitHubAvatar size={40} src={viewerData.avatarUrl} alt="" />
            </div>
          )}
          <span className={styles.collapsedInput}>
            <span className={styles.collapsedInputText}>
              {markdownBody.length > 0 ? markdownBody.split('\n')[0] : LABELS.newCommentPlaceholder}
            </span>
            <span className={styles.collapsedEditIcon} aria-hidden="true">
              <PencilIcon size={16} />
            </span>
          </span>
        </button>
      )}
      <div className={inlineAvatars ? styles.commentComposerInline : styles.commentComposer}>
        {inlineAvatars ? (
          <div
            className={clsx(styles.commentBoxWrapper, styles.commentBoxWrapper_inlineAvatars, bannerPaddingClassName)}
          >
            <div className={styles.headingRow}>
              <Heading
                data-jump-to-bottom-target={!stickyCommentComposer || undefined}
                tabIndex={!stickyCommentComposer ? -1 : undefined}
                id="comment-composer-heading"
                as="h2"
                className={styles.headingInline}
              >
                Add a comment
              </Heading>
              {stickyCommentComposer && viewerCanComment && composerExpanded && hasDraft && (
                <IconButton
                  variant="invisible"
                  size="small"
                  icon={ChevronDownIcon}
                  onClick={collapseComposer}
                  aria-label="Collapse comment composer"
                  className={styles.collapseButton}
                />
              )}
              {resizeHandle}
            </div>
            {commentBoxContent}
          </div>
        ) : (
          <>
            <Link
              href={`/${viewerData.login}`}
              {...getHovercardAttributesForType('user', {login: viewerData.login})}
              aria-label={`@${viewerData.login}'s profile`}
              className={styles.avatarLink}
            >
              <GitHubAvatar size={40} src={viewerData.avatarUrl} alt={`@${viewerData.login}`} />
            </Link>
            <Heading
              data-jump-to-bottom-target={!stickyCommentComposer || undefined}
              tabIndex={!stickyCommentComposer ? -1 : undefined}
              id="comment-composer-heading"
              as="h2"
              className={styles.heading}
            >
              Add a comment
            </Heading>
            {stickyCommentComposer && viewerCanComment && composerExpanded && hasDraft && (
              <IconButton
                variant="invisible"
                size="small"
                icon={ChevronDownIcon}
                onClick={collapseComposer}
                aria-label="Collapse comment composer"
                className={styles.collapseButton}
              />
            )}
            {resizeHandle}
            <div className={clsx(styles.commentBoxWrapper, bannerPaddingClassName)}>{commentBoxContent}</div>
          </>
        )}
      </div>
    </div>
  )
}
IssueCommentComposer.displayName = 'CommentComposer'

const CommentDisabledDueToDiscussion = ({discussionUrl}: {discussionUrl: string}) => (
  <div>
    {LABELS.issueConvertedToDiscussion}&nbsp;
    <Link href={discussionUrl} inline>
      discussion
    </Link>
  </div>
)
